import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Input,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Select,
  Stack,
  Text,
  Divider,
} from "@chakra-ui/react";
import type React from "react";
import { useEffect, useState } from "react";
import type { MarriageProfileSearchItem, SearchFilters } from "src/types/matrimonySearchResults.types";
import { calculateAge, formatHeight } from "src/utils/matrimonySearchResults.utils";
import { NativePlaceIconOrange, RasiNakshatraIconOrange, WorkIconOrange } from "src/resources/icons";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import {
  DIETARY_HABITS_OPTIONS,
  EMPLOYMENT_SECTOR_OPTIONS,
  HIGHEST_EDUCATION_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  NAKSHATRA_OPTIONS,
  RASI_OPTIONS,
  getOptionLabel,
} from "src/utils/matrimonyOptions";

export const FilterSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="600" color="#6C757D" mb={2}>
        {title}
      </Text>
      {children}
    </Box>
  );
};

export const RangeFilter = ({
  title,
  min,
  max,
  valueMin,
  valueMax,
  onChangeMin,
  onChangeMax,
  renderValue,
}: {
  title: string;
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  onChangeMin: (v: number) => void;
  onChangeMax: (v: number) => void;
  renderValue?: (lo: number, hi: number) => React.ReactNode;
}) => {
  const lo = Math.min(valueMin, valueMax);
  const hi = Math.max(valueMin, valueMax);

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={2}>
        <Text fontSize="xs" fontWeight="600" color="#6C757D">
          {title}
        </Text>
        <Text fontSize="xs" color="#6C757D">
          {renderValue ? renderValue(lo, hi) : `${lo} - ${hi}`}
        </Text>
      </Flex>

      <RangeSlider
        aria-label={[`${title}-min`, `${title}-max`] as [string, string]}
        min={min}
        max={max}
        value={[lo, hi]}
        onChange={(vals) => {
          const [a, b] = vals;
          onChangeMin(a);
          onChangeMax(b);
        }}
      >
        <RangeSliderTrack>
          <RangeSliderFilledTrack bg="#FF7E6B" />
        </RangeSliderTrack>
        <RangeSliderThumb index={0} />
        <RangeSliderThumb index={1} />
      </RangeSlider>
    </Box>
  );
};

export const FiltersPanel = ({
  value,
  onChange,
  onApply,
  onClear,
  isMobile,
}: {
  value: SearchFilters;
  onChange: (next: SearchFilters) => void;
  onApply: () => void;
  onClear: () => void;
  isMobile: boolean;
}) => {
  const boxBg = "#F8F9FA";
  const border = "rgba(108, 117, 125, 0.3)";

  // Sensible UI bounds; backend will still receive user-chosen values only.
  const ageMin = 18;
  const ageMax = 70;
  const heightMin = 120;
  const heightMax = 220;

  // Defaults when a user hasn't chosen explicit bounds.
  const defaultAgeFrom = 25;
  const defaultAgeTo = 34;
  const defaultHeightFrom = 147;
  const defaultHeightTo = 178;

  const ageFrom = value.minAge ?? defaultAgeFrom;
  const ageTo = value.maxAge ?? defaultAgeTo;
  const heightFrom = value.minHeight ?? defaultHeightFrom;
  const heightTo = value.maxHeight ?? defaultHeightTo;

  return (
    <Stack spacing={5} marginTop={0}>
      <Flex align="center" justify="space-between">
        <Text fontWeight="700">Filters</Text>
        <Button variant="ghost" size="sm" onClick={onClear} color="#6C757D">
          Clear all
        </Button>
      </Flex>

      <FilterSection title="Location">
        <Input
          bg={boxBg}
          borderRadius="12px"
          borderColor={border}
          height="44px"
          placeholder="Search location"
          value={value.nativePlace || ""}
          onChange={(e) => onChange({ ...value, nativePlace: e.target.value, page: 0 })}
          aria-label="Location"
        />
      </FilterSection>

      <FilterSection title="Highest Education">
        <Select
          bg={boxBg}
          borderRadius="12px"
          borderColor={border}
          height="44px"
          placeholder="Select"
          value={value.highestEducation || ""}
          onChange={(e) => onChange({ ...value, highestEducation: e.target.value || undefined, page: 0 })}
          aria-label="Highest Education"
        >
          {HIGHEST_EDUCATION_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Select>
      </FilterSection>

      <FilterSection title="Employment Sector">
        <Select
          bg={boxBg}
          borderRadius="12px"
          borderColor={border}
          height="44px"
          placeholder="Select"
          value={value.employmentSector || ""}
          onChange={(e) => onChange({ ...value, employmentSector: e.target.value || undefined, page: 0 })}
          aria-label="Employment Sector"
        >
          {EMPLOYMENT_SECTOR_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FilterSection>

      <FilterSection title="Marital Status">
        <Select
          bg={boxBg}
          borderRadius="12px"
          borderColor={border}
          height="44px"
          placeholder="Select"
          value={value.maritalStatus || ""}
          onChange={(e) => onChange({ ...value, maritalStatus: e.target.value || undefined, page: 0 })}
          aria-label="Marital Status"
        >
          {MARITAL_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FilterSection>

      <Box>
        <Flex align="center" justify="space-between" mb={2}>
          <Text fontSize="xs" fontWeight="600" color="#6C757D">
            Height
          </Text>
          <Button
            variant="ghost"
            size="xs"
            color="#6C757D"
            onClick={() =>
              onChange({
                ...value,
                minHeight: defaultHeightFrom,
                maxHeight: defaultHeightTo,
                page: 0,
              })
            }
          >
            Reset
          </Button>
        </Flex>

        <RangeFilter
          title=""
          min={heightMin}
          max={heightMax}
          valueMin={heightFrom}
          valueMax={heightTo}
          onChangeMin={(v) => onChange({ ...value, minHeight: v, page: 0 })}
          onChangeMax={(v) => onChange({ ...value, maxHeight: v, page: 0 })}
          renderValue={(lo, hi) => `${formatHeight(lo)} - ${formatHeight(hi)}`}
        />
      </Box>

      <Box>
        <Flex align="center" justify="space-between" mb={2}>
          <Text fontSize="xs" fontWeight="600" color="#6C757D">
            Age
          </Text>
          <Button
            variant="ghost"
            size="xs"
            color="#6C757D"
            onClick={() =>
              onChange({
                ...value,
                minAge: defaultAgeFrom,
                maxAge: defaultAgeTo,
                page: 0,
              })
            }
          >
            Reset
          </Button>
        </Flex>

        <RangeFilter
          title=""
          min={ageMin}
          max={ageMax}
          valueMin={ageFrom}
          valueMax={ageTo}
          onChangeMin={(v) => onChange({ ...value, minAge: v, page: 0 })}
          onChangeMax={(v) => onChange({ ...value, maxAge: v, page: 0 })}
          renderValue={(lo, hi) => `${lo} - ${hi} yrs`}
        />
      </Box>

      <FilterSection title="Smoking">
        <Select
          bg={boxBg}
          borderRadius="12px"
          borderColor={border}
          height="44px"
          value={value.smoking === undefined ? "" : value.smoking ? "true" : "false"}
          onChange={(e) => {
            const v = e.target.value;
            onChange({
              ...value,
              smoking: v === "" ? undefined : v === "true",
              page: 0,
            });
          }}
          aria-label="Smoking"
        >
          <option value="">Any</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      </FilterSection>

      <FilterSection title="Drinking">
        <Select
          bg={boxBg}
          borderRadius="12px"
          borderColor={border}
          height="44px"
          value={value.drinking === undefined ? "" : value.drinking ? "true" : "false"}
          onChange={(e) => {
            const v = e.target.value;
            onChange({
              ...value,
              drinking: v === "" ? undefined : v === "true",
              page: 0,
            });
          }}
          aria-label="Drinking"
        >
          <option value="">Any</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      </FilterSection>

      <FilterSection title="Dietary Habits">
        <Select
          bg={boxBg}
          borderRadius="12px"
          borderColor={border}
          height="44px"
          placeholder="Select"
          value={value.dietaryHabits || ""}
          onChange={(e) => onChange({ ...value, dietaryHabits: e.target.value || undefined, page: 0 })}
          aria-label="Dietary Habits"
        >
          {DIETARY_HABITS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FilterSection>

      {!isMobile ? (
        <Button bg="#4A2C2A" color="white" _hover={{ bg: "#3A211F" }} borderRadius="12px" onClick={onApply}>
          Apply Filters
        </Button>
      ) : null}
    </Stack>
  );
};

export const ProfileCard = ({
  profile,
  onSeeDetails,
  isPending = false,
}: {
  profile: MarriageProfileSearchItem;
  onSeeDetails: (id: string | number) => void;
  isPending?: boolean;
}) => {
  const computedAge = profile.age ?? calculateAge(profile.dateOfBirth);
  const ageText = computedAge != null ? `${computedAge} Yrs` : "";
  const heightText = profile.heightInCm != null ? formatHeight(profile.heightInCm) : "";

  const isInactive = profile.active === false;

  const work = [profile.designation, profile.employerName].filter(Boolean).join(", ");
  const rasiNakshatra = [
    profile.rasi ? `Rashi: ${getOptionLabel(RASI_OPTIONS, profile.rasi)}` : null,
    profile.nakshatra ? `Nakshatra: ${getOptionLabel(NAKSHATRA_OPTIONS, profile.nakshatra)}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    const rawFileName = profile.profilePhoto;
    if (!rawFileName) {
      setPhotoUrl(undefined);
      return;
    }

    (async () => {
      try {
        const url = await fetchAuthedImageObjectUrl(rawFileName);
        createdUrl = url;
        if (!cancelled) setPhotoUrl(url);
        else if (createdUrl) URL.revokeObjectURL(createdUrl);
      } catch {
        if (!cancelled) setPhotoUrl(undefined);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) {
        try {
          URL.revokeObjectURL(createdUrl);
        } catch {
          // ignore
        }
      }
    };
  }, [profile.profilePhoto]);

  return (
    <Box
      w="full"
      bg="white"
      borderRadius="16px"
      boxShadow="0px 17px 38px rgba(120, 120, 120, 0.10)"
      border="1px solid rgba(108, 117, 125, 0.18)"
      overflow="hidden"
      opacity={isInactive ? 0.6 : 1}
      filter={isInactive ? "grayscale(0.15)" : undefined}
    >
      <Box p={4}>
        <Flex align="flex-start" gap={4}>
          <Avatar
            name={profile.fullName || "Marriage Profile"}
            src={photoUrl}
            size={{ base: "lg", md: "xl" }}
          />

          <Box minW={0} flex="1">
            <Flex align="start" justify="space-between" gap={3}>
              <Box minW={0}>
                <Text fontWeight="700" color="#1F0812" noOfLines={1} fontSize={{ base: "md", md: "lg" }}>
                  {profile.fullName || "—"}
                </Text>

                {(ageText || heightText) ? (
                  <Text fontSize="sm" color="#6C757D" noOfLines={1}>
                    {ageText}
                    {ageText && heightText ? " " : ""}
                    {heightText}
                  </Text>
                ) : null}
              </Box>

              <Stack direction="row" spacing={2} align="center" flexShrink={0}>
                <Badge
                  borderRadius="999px"
                  px={3}
                  py={1}
                  bg={isInactive ? "rgba(108,117,125,0.15)" : "rgba(39,174,96,0.12)"}
                  color={isInactive ? "#6C757D" : "#1E7A45"}
                >
                  {isInactive ? "Inactive" : "Active"}
                </Badge>

                {profile.alumniMatch ? (
                  <Badge
                    borderRadius="999px"
                    px={3}
                    py={1}
                    bg="rgba(255,126,107,0.15)"
                    color="#FF7E6B"
                  >
                    Alumni Match
                  </Badge>
                ) : null}
              </Stack>
            </Flex>

            {work ? (
              <Flex align="center" gap={2} mt={2} minW={0}>
                <WorkIconOrange width={18} height={18} />
                <Text fontSize="sm" color="#1F0812" noOfLines={1}>
                  {work}
                </Text>
              </Flex>
            ) : null}

            {profile.nativePlace ? (
              <Flex align="center" gap={2} mt={1} minW={0}>
                <NativePlaceIconOrange width={18} height={18} />
                <Text fontSize="sm" color="#6C757D" noOfLines={1}>
                  {profile.nativePlace}
                </Text>
              </Flex>
            ) : null}

            {rasiNakshatra ? (
              <Flex align="center" gap={2} mt={2} minW={0}>
                <RasiNakshatraIconOrange width={18} height={18} />
                <Text fontSize="xs" color="#6C757D" noOfLines={1}>
                  {rasiNakshatra}
                </Text>
              </Flex>
            ) : null}
          </Box>
        </Flex>

        {profile.alumniMatch ? (
          <Button
            mt={4}
            size="sm"
            variant="outline"
            borderRadius="999px"
            borderColor="rgba(255,126,107,0.35)"
            color="#4A2C2A"
            bg="rgba(255,126,107,0.08)"
            _hover={{ bg: "rgba(255,126,107,0.12)" }}
            isDisabled={isInactive}
          >
            Alumni Match
          </Button>
        ) : null}
      </Box>

      <Divider />

      <Flex justify="center" py={3}>
        {isPending ? (
          <Badge
            borderRadius="full"
            px={4}
            py={2}
            fontSize="sm"
            fontWeight="600"
            bg="rgba(255, 165, 0, 0.12)"
            color="#B45309"
          >
            Interest Request Pending
          </Badge>
        ) : (
          <Button
            variant="ghost"
            fontWeight="600"
            color="#4A2C2A"
            onClick={() => onSeeDetails(profile.id)}
          >
            See Details
          </Button>
        )}
      </Flex>
    </Box>
  );
};
